<script lang="ts">
  // StateBadge — colored pill mapping a container state to a tone + label.
  //
  // Visual reference: openspec/changes/ui-redesign-v2/Untitled.fig
  // nodes 1:720-740 (state pills).
  //
  // The badge is the single visual cue for "is this container healthy?".
  // Three tones cover every state:
  //   - ok   (running)            --ok-green   text on --ok-bg
  //   - warn (restarting, paused,
  //           created, removing,
  //           unknown)            --warn-amber text on --warn-bg
  //   - err  (exited, dead)       --err-red    text on --err-bg
  //
  // Loading + null render a skeleton variant so the row stays calm
  // before the first snapshot frame arrives.

  export let state: string | null;

  type Tone = "ok" | "warn" | "err" | "loading";

  const OK_STATES = new Set(["running"]);
  const ERR_STATES = new Set(["exited", "dead"]);
  const WARN_STATES = new Set([
    "restarting",
    "paused",
    "created",
    "removing",
  ]);

  function toneFor(s: string | null): Tone {
    if (s === null || s === "loading") return "loading";
    if (OK_STATES.has(s)) return "ok";
    if (ERR_STATES.has(s)) return "err";
    if (WARN_STATES.has(s)) return "warn";
    return "warn";
  }

  function labelFor(s: string | null): string {
    if (s === null || s === "loading") return "loading";
    if (s === "") return "unknown";
    if (OK_STATES.has(s) || ERR_STATES.has(s) || WARN_STATES.has(s)) return s;
    return "unknown";
  }

  $: tone = toneFor(state);
  $: label = labelFor(state);
</script>

<span
  class="state-badge state-badge--{tone}"
  data-state={state ?? "loading"}
  data-tone={tone}
  data-testid="state-badge"
  aria-label={`State: ${label}`}
>
  <span class="state-badge__dot" aria-hidden="true"></span>
  <span class="state-badge__label">{label}</span>
</span>

<style>
  .state-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 22px;
    padding: 0 8px;
    border-radius: 999px;
    border: 1px solid var(--border);
    background: var(--surface-2);
    color: var(--text-mid);
    font-family: "Inter", system-ui, sans-serif;
    font-size: 11px;
    font-weight: 500;
    line-height: 1;
    text-transform: lowercase;
    letter-spacing: 0.02em;
    white-space: nowrap;
  }

  .state-badge__dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--text-mid);
    flex: 0 0 6px;
  }

  .state-badge--ok {
    color: var(--ok-green);
    border-color: rgba(34, 197, 94, 0.40);
    background: var(--ok-bg);
  }
  .state-badge--ok .state-badge__dot {
    background: var(--ok-green);
    box-shadow: 0 0 6px rgba(34, 197, 94, 0.55);
  }

  .state-badge--warn {
    color: var(--warn-amber);
    border-color: rgba(245, 158, 11, 0.40);
    background: var(--warn-bg);
  }
  .state-badge--warn .state-badge__dot {
    background: var(--warn-amber);
  }

  .state-badge--err {
    color: var(--err-red);
    border-color: rgba(239, 68, 68, 0.40);
    background: var(--err-bg);
  }
  .state-badge--err .state-badge__dot {
    background: var(--err-red);
    box-shadow: 0 0 6px rgba(239, 68, 68, 0.55);
  }

  .state-badge--loading {
    color: var(--text-dim);
    border-style: dashed;
    background: transparent;
  }
  .state-badge--loading .state-badge__dot {
    background: var(--surface-3);
    box-shadow: none;
  }
</style>